import React from 'react';

import { Text } from '@/nova/components';

import { useReducedMotion } from '@/nova/hooks/useReducedMotion';
import { STORYBOARD_COMPOSE } from '@/nova/theme/tokens';
import { useTypewriter } from '@/nova/pages/pulse/components/PulseCalibrating/useTypewriter';

import { LumioMorph } from '@/nova/pages/home/components/BrandDeploymentStrip/LumioMorph';
import { Caret, Line, Mark, Phrase, Suffix, Words } from './RunStatusLine.styles';

export interface RunStatusLineProps {
  /** The current step's phrasings, already translated (first = plainest). One phrase types in and rests;
   *  two or more cycle (type, hold, erase, next) for as long as the step lasts. */
  phrases: string[];
  /** Static trailing label, e.g. the video model in parentheses; not typed. */
  suffix?: string;
  /** The mark above the words instead of before them, both centred: the mark holds still while the phrase types in
   *  (inline, a centred line re-centres with every character and the mark drifts). */
  stacked?: boolean;
}

/**
 * The live "working on it" line for a video-ad run: the morphing Lumio mark, then the current step's
 * phrasings typed out and cycled with a caret, then a static suffix. The caller KEYS it by the step, so a new
 * step remounts it and types in immediately instead of waiting out the previous step's cycle. Under reduced
 * motion the phrases rotate whole, with no typing or caret, and the mark holds still.
 */
export const RunStatusLine = ({ phrases, suffix, stacked = false }: RunStatusLineProps): React.ReactElement => {
  const reduced = useReducedMotion();
  const { text, busy } = useTypewriter(phrases, {
    typeMs: STORYBOARD_COMPOSE.STATUS_TYPE_MS,
    deleteMs: STORYBOARD_COMPOSE.STATUS_DELETE_MS,
    holdMs: STORYBOARD_COMPOSE.STATUS_HOLD_MS,
    gapMs: STORYBOARD_COMPOSE.STATUS_GAP_MS,
    reduced,
  });
  return (
    // Assistive tech hears the step once (its plainest phrasing + the suffix), never the per-character typing.
    <Line
      role="status"
      aria-live="polite"
      aria-label={suffix ? `${phrases[0] ?? ''} ${suffix}` : phrases[0]}
      $stacked={stacked}
    >
      <Mark aria-hidden="true">
        {/* The Lumio mark in its "working" state (the deploy strip's morph into the strands flourish), never an
            approximated sparkle/star. It holds still under reduced motion. */}
        <LumioMorph size={STORYBOARD_COMPOSE.STATUS_MARK_PX} />
      </Mark>
      <Words aria-hidden="true">
        <Phrase raw text={text} />
        <Caret $busy={busy} />
      </Words>
      {suffix ? (
        <Suffix aria-hidden="true">
          <Text raw text={suffix} tag="span" />
        </Suffix>
      ) : null}
    </Line>
  );
};

export default RunStatusLine;
